import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Badge, Button, Select } from '../atoms';
import { PageHeader } from '../shell';
import { Tabs } from '../organisms';
import { useUserNames } from '../lib/useUserNames';
import { money } from '../organisms/CommissionArrived';

/**
 * ArReport — /ar (ATA part 4 §6, part 3 §4). Tony's ask: accounts receivable the way ATA's
 * accounting runs it, in the three shapes their spreadsheets already take.
 *   All clients   every client with an open balance, Collections as a badge
 *   Aging         per client: Current 1 to 30, 31 to 60, 61 to 90, 91 and over, total
 *   Client detail one client's open invoices grouped by bucket (the drill-down from either)
 * Filter by Manager; export CSV, xlsx or PDF in the sample column order. The server decides who
 * sees which clients; the money-blind role gets the shape without the dollars.
 */

interface SummaryRow { client_id: string; client_name: string; in_collections: boolean; manager: string; invoices: number; total_open?: number }
interface AgingRow { client_id: string; client_name: string; in_collections: boolean; manager: string; bucket_current?: number; bucket_1_30?: number; bucket_31_60?: number; bucket_61_90?: number; bucket_91_plus?: number; total_open?: number }
interface DetailRow { invoice_id: string; invoice_number: string | null; invoice_date: string | null; due_date: string | null; memo: string; type_label: string; assessor: string | null; state: string | null; days_past_due: number; amount?: number; open_balance?: number }
interface Summary { as_of: string; rows: SummaryRow[]; total_open?: number }
interface Aging { as_of: string; rows: AgingRow[]; totals: Record<string, number | undefined> }
interface Detail { as_of: string; client: { id: string; name: string; in_collections: boolean; manager: string | null } | null; buckets: Array<{ key: string; label: string; rows: DetailRow[]; bucket_subtotal?: number }>; total_open?: number }

const table = { width: '100%', borderCollapse: 'collapse' as const, fontSize: 'var(--fs-sm)', background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)' };
const th = { textAlign: 'left' as const, padding: 'var(--sp-2) var(--sp-3)', borderBottom: '1px solid var(--color-border)', color: 'var(--color-ink-3)', fontWeight: 'var(--fw-medium)' as const };
const thR = { ...th, textAlign: 'right' as const };
const td = { padding: 'var(--sp-2) var(--sp-3)', borderBottom: '1px solid var(--color-border)', color: 'var(--color-ink)' };
const tdR = { ...td, textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' as const };

export function ArReport() {
  const navigate = useNavigate();
  const users = useUserNames();
  const [tab, setTab] = useState('summary');
  const [manager, setManager] = useState('');
  const [clientId, setClientId] = useState<string | null>(null);
  const qs = manager ? `?manager_user_id=${manager}` : '';

  const summary = useQuery<Summary>({ queryKey: ['ar-summary', manager], queryFn: ({ signal }) => api.get(`/api/ar/summary${qs}`, signal) });
  const aging = useQuery<Aging>({ queryKey: ['ar-aging', manager], queryFn: ({ signal }) => api.get(`/api/ar/aging${qs}`, signal) });
  const detail = useQuery<Detail>({ queryKey: ['ar-detail', clientId], queryFn: ({ signal }) => api.get(`/api/ar/aging/${clientId}`, signal), enabled: !!clientId });

  const exportHref = (fmt: 'csv' | 'xlsx' | 'pdf') => {
    const base = tab === 'detail' && clientId ? `/api/ar/aging/${clientId}` : tab === 'aging' ? '/api/ar/aging' : '/api/ar/summary';
    const q = new URLSearchParams(); if (manager) q.set('manager_user_id', manager); q.set('format', fmt);
    return `${base}?${q.toString()}`;
  };
  const openClient = (id: string) => { setClientId(id); setTab('detail'); };
  const asOf = useMemo(() => (tab === 'aging' ? aging.data?.as_of : tab === 'detail' ? detail.data?.as_of : summary.data?.as_of), [tab, aging.data, detail.data, summary.data]);

  return (
    <>
      <PageHeader
        title="Accounts receivable"
        subtitle={asOf ? `As of ${asOf}. Open invoices only; days past the due date.` : 'Loading…'}
        actions={
          <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
            <Button variant="secondary" size="md" onClick={() => window.location.assign(exportHref('csv'))}>Export CSV</Button>
            <Button variant="secondary" size="md" onClick={() => window.location.assign(exportHref('xlsx'))}>Export xlsx</Button>
            <Button variant="secondary" size="md" onClick={() => window.location.assign(exportHref('pdf'))}>Export PDF</Button>
          </div>
        }
      />
      <div style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'flex-end', marginBottom: 'var(--sp-4)' }}>
        <label style={{ display: 'grid', gap: 'var(--sp-1)' }}>
          <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>Manager</span>
          <Select aria-label="Manager" value={manager} onChange={(e) => setManager(e.target.value)} style={{ minWidth: 200 }} options={[{ value: '', label: 'Every manager' }, ...users.options]} />
        </label>
      </div>
      <Tabs
        items={[
          { value: 'summary', label: 'All clients', count: summary.data?.rows.length },
          { value: 'aging', label: 'Aging by client', count: aging.data?.rows.length },
          { value: 'detail', label: detail.data?.client ? `Detail: ${detail.data.client.name}` : 'Client detail', disabled: !clientId },
        ]}
        activeValue={tab}
        onChange={setTab}
      />
      <div style={{ marginTop: 'var(--sp-4)' }}>
        {tab === 'summary' && (summary.data ? (
          summary.data.rows.length === 0 ? <Empty>No client has an open balance{manager ? ' for this manager' : ''}.</Empty> : (
            <table style={table} data-testid="ar-summary">
              <thead><tr><th style={th}>Client</th><th style={th}>Manager</th><th style={thR}>Open invoices</th><th style={thR}>Total</th></tr></thead>
              <tbody>
                {summary.data.rows.map(r => (
                  <tr key={r.client_id} onClick={() => openClient(r.client_id)} style={{ cursor: 'pointer' }}>
                    <td style={td}><strong>{r.client_name}</strong> {r.in_collections && <Badge variant="danger">In Collections</Badge>}</td>
                    <td style={td}>{r.manager || '—'}</td>
                    <td style={tdR}>{r.invoices}</td>
                    <td style={tdR}>{money(r.total_open)}</td>
                  </tr>
                ))}
                <tr><td style={td} colSpan={3}><strong>Total</strong></td><td style={tdR}><strong>{money(summary.data.total_open)}</strong></td></tr>
              </tbody>
            </table>
          )
        ) : <p style={{ color: 'var(--color-ink-3)' }}>Loading…</p>)}

        {tab === 'aging' && (aging.data ? (
          aging.data.rows.length === 0 ? <Empty>No client has an open balance{manager ? ' for this manager' : ''}.</Empty> : (
            <table style={table} data-testid="ar-aging">
              <thead><tr><th style={th}>Client Name</th><th style={thR}>Current 1 to 30</th><th style={thR}>31 to 60</th><th style={thR}>61 to 90</th><th style={thR}>91 and over</th><th style={thR}>Total</th></tr></thead>
              <tbody>
                {aging.data.rows.map(r => (
                  <tr key={r.client_id} onClick={() => openClient(r.client_id)} style={{ cursor: 'pointer' }}>
                    <td style={td}><strong>{r.client_name}</strong> {r.in_collections && <Badge variant="danger">In Collections</Badge>}</td>
                    <td style={tdR}>{money(r.bucket_current == null || r.bucket_1_30 == null ? undefined : r.bucket_current + r.bucket_1_30)}</td>
                    <td style={tdR}>{money(r.bucket_31_60)}</td><td style={tdR}>{money(r.bucket_61_90)}</td><td style={tdR}>{money(r.bucket_91_plus)}</td>
                    <td style={tdR}><strong>{money(r.total_open)}</strong></td>
                  </tr>
                ))}
                <tr>
                  <td style={td}><strong>Totals</strong></td>
                  <td style={tdR}>{money(aging.data.totals.bucket_current == null || aging.data.totals.bucket_1_30 == null ? undefined : (aging.data.totals.bucket_current ?? 0) + (aging.data.totals.bucket_1_30 ?? 0))}</td>
                  <td style={tdR}>{money(aging.data.totals.bucket_31_60)}</td><td style={tdR}>{money(aging.data.totals.bucket_61_90)}</td><td style={tdR}>{money(aging.data.totals.bucket_91_plus)}</td>
                  <td style={tdR}><strong>{money(aging.data.totals.total_open)}</strong></td>
                </tr>
              </tbody>
            </table>
          )
        ) : <p style={{ color: 'var(--color-ink-3)' }}>Loading…</p>)}

        {tab === 'detail' && (detail.data ? (
          <div style={{ display: 'grid', gap: 'var(--sp-4)' }}>
            {detail.data.client && (
              <div style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'center' }}>
                <button type="button" onClick={() => navigate(`/clients/${detail.data!.client!.id}?tab=billing`)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--brand-primary)', fontWeight: 'var(--fw-medium)', padding: 0, fontSize: 'var(--fs-md)' }}>{detail.data.client.name}</button>
                {detail.data.client.in_collections && <Badge variant="danger">In Collections</Badge>}
                {detail.data.client.manager && <span style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>Manager {detail.data.client.manager}</span>}
              </div>
            )}
            {detail.data.buckets.map(b => (
              <section key={b.key} data-testid={`bucket-${b.key}`}>
                <h3 style={{ margin: '0 0 var(--sp-2)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' }}>{b.label}</h3>
                {b.rows.length === 0 ? <div style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>Nothing in this bucket.</div> : (
                  <table style={table}>
                    <thead><tr><th style={th}>Date</th><th style={th}>Num</th><th style={th}>Memo</th><th style={th}>Type</th><th style={th}>Assessor</th><th style={th}>Due</th><th style={thR}>Days</th><th style={thR}>Amount</th><th style={thR}>Open balance</th></tr></thead>
                    <tbody>
                      {b.rows.map(r => (
                        <tr key={r.invoice_id} onClick={() => navigate(`/invoices/${r.invoice_id}`)} style={{ cursor: 'pointer' }}>
                          <td style={td}>{r.invoice_date ?? '—'}</td><td style={td}>{r.invoice_number ?? '—'}</td><td style={td}>{r.memo}</td><td style={td}>{r.type_label}</td>
                          <td style={td}>{r.assessor ?? '—'}</td><td style={td}>{r.due_date ?? '—'}</td><td style={tdR}>{r.days_past_due}</td><td style={tdR}>{money(r.amount)}</td><td style={tdR}>{money(r.open_balance)}</td>
                        </tr>
                      ))}
                      <tr><td style={td} colSpan={8}><strong>Total for {b.label}</strong></td><td style={tdR}><strong>{money(b.bucket_subtotal)}</strong></td></tr>
                    </tbody>
                  </table>
                )}
              </section>
            ))}
            <div style={{ textAlign: 'right', fontWeight: 'var(--fw-semibold)' }}>Total open {money(detail.data.total_open)}</div>
          </div>
        ) : <p style={{ color: 'var(--color-ink-3)' }}>{clientId ? 'Loading…' : 'Pick a client from All clients or Aging by client.'}</p>)}
      </div>
    </>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: 'var(--sp-4)', color: 'var(--color-ink-3)', border: '1px dashed var(--color-border)', borderRadius: 'var(--r-lg)' }}>{children}</div>;
}
